import React, { useEffect, useMemo, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { Plus, Download, RefreshCw, ShieldCheck, Globe, PlugZap, Activity, Gauge, Eye, AlertTriangle, FileText, Zap, Wrench, History, ArrowLeft, CheckCircle2, Smartphone, Languages, Share2, AtSign, ChevronLeft, ChevronRight, Link2, Code, AlertOctagon, BookOpen } from 'lucide-react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useParams } from 'react-router-dom';
import Login from './components/Login';
import Sidebar from './components/Sidebar';
import Profile from './components/Profile.jsx';
import TeamSettings from './components/TeamSettings.jsx';
import InviteAccept from './components/InviteAccept.jsx';
import RedirectsTab from './components/RedirectsTab.jsx';
import Runbook from './components/Runbook.jsx';
import './styles.css';


const ISSUE_TYPE_META = {
  // Per-page metadata (audit roadmap Step 3 issue types)
  viewport_missing: {
    Icon: Smartphone,
    label: 'Missing viewport',
    description: 'This page does not declare a viewport meta tag. Mobile browsers will fall back to a 980px desktop layout, which makes the page unreadable on phones and hurts mobile search rankings.'
  },
  viewport_invalid: {
    Icon: Smartphone,
    label: 'Invalid viewport',
    description: 'A viewport meta tag is present but the value is not recognized. Replace it with width=device-width, initial-scale=1 so the page scales correctly on mobile devices.'
  },
  html_lang_missing: {
    Icon: Languages,
    label: 'Missing html lang',
    description: 'The <html> tag has no lang attribute. Search engines and screen readers use this to determine the page language, so missing it hurts both accessibility and international SEO.'
  },

  // Cross-page / sitewide consistency (audit roadmap Step 4)
  og_site_name_inconsistent: {
    Icon: Share2,
    label: 'Inconsistent OG site name',
    description: 'Pages are declaring different og:site_name values (likely from different templates or plugins). Social shares will show inconsistent branding in Facebook, LinkedIn, Slack, Discord, and any other Open Graph consumer.'
  },
  og_site_name_missing_sitewide: {
    Icon: Share2,
    label: 'OG site name missing (sitewide)',
    description: 'Most pages with Open Graph tags do not declare og:site_name. This usually means the global SEO plugin setting is empty — fill it in once at the site level and the tag will propagate to every page.'
  },
  og_image_missing_sitewide: {
    Icon: Share2,
    label: 'OG image missing (sitewide)',
    description: 'Most pages with Open Graph tags do not declare og:image. Social shares without an image get dramatically less engagement. Set a default OG image in your SEO plugin, or configure a featured-image fallback in your theme.'
  },
  twitter_card_missing_sitewide: {
    Icon: AtSign,
    label: 'Twitter card missing (sitewide)',
    description: 'Most pages with Twitter tags do not declare twitter:card. Twitter/X cannot render a rich card without it, so the link preview falls back to plain text. Add twitter:card="summary_large_image" globally.'
  },
  twitter_card_inconsistent: {
    Icon: AtSign,
    label: 'Inconsistent Twitter card type',
    description: 'Pages are declaring different twitter:card values (e.g. "summary" vs "summary_large_image"). Same-content-type pages should use the same card type so social previews are predictable.'
  },

  // High-ROI cross-page rules (post-assessment extension).
  // These target Google ranking and indexing factors, not social previews.
  canonical_pattern_inconsistent: {
    Icon: Link2,
    label: 'Canonical host pattern split',
    description: 'Pages are declaring different canonical host patterns (e.g. https://www.example.com vs https://example.com, or http vs https). Google treats these as distinct URLs and will index both, fragmenting PageRank and triggering duplicate-content signals. Pick one canonical host and add 301 redirects from the others to it.'
  },
  hreflang_not_bidirectional: {
    Icon: Languages,
    label: 'Hreflang not bidirectional',
    description: 'For every hreflang link on a page, the target page must include a matching hreflang link back. Google ignores non-reciprocal hreflang clusters, so unidirectional links silently disable international targeting.'
  },
  soft_404: {
    Icon: AlertOctagon,
    label: 'Soft 404 (empty 200 page)',
    description: 'The page returns HTTP 200 but its body matches common empty-state copy ("no results found", "product out of stock", "page not found"). Googlebot treats these as low-value and the pages waste crawl budget. Return 404 for genuinely missing content or render real content.'
  },
  json_ld_validation: {
    Icon: Code,
    label: 'JSON-LD validation error',
    description: 'Structured data is missing required fields for the declared @type (e.g. a Product without image/name/description). Google will not show rich results for incomplete types. Fix the @type block to make the page eligible for product, article, or FAQ rich results.'
  },
};

function issueTypeMeta(t) {
  return ISSUE_TYPE_META[t] || { Icon: FileText, label: t || 'Issue', description: '' };
}


async function safeJson(res) {
  const text = await res.text();
  try {
    return JSON.parse(text || '{}');
  } catch (e) {
    const preview = String(text || '').replace(/\s+/g, ' ').slice(0, 220);
    throw new Error(`API returned non-JSON response (${res.status} ${res.statusText || 'HTTP'}): ${preview || 'empty response'}`);
  }
}

async function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function notify(message, type = 'info') {
  window.dispatchEvent(new CustomEvent('sm-toast', { detail: { message, type } }));
}

const platforms = ['wordpress', 'magento', 'laravel', 'generic_php', 'shopify', 'static'];
const serverTypes = ['apache_litespeed', 'nginx', 'cloudflare_pages', 'unknown'];

function App() {
  if (window.location.pathname.startsWith('/invite/')) return <InviteAccept />;
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const res = await fetch('/api/auth/me');
        if (res.ok) {
          const data = await res.json();
          setUser(data.user || data);
          setIsAuthenticated(true);
        }
      } catch (e) {
        console.error('Auth check failed', e);
      } finally {
        setAuthLoading(false);
      }
    };
    checkAuth();
  }, []);

  async function handleLogout() {
    // Native-logout flow. There is no Cloudflare Access layer — the
    // session_id cookie (set by /api/auth/login) IS the only auth
    // token. To make a browser refresh force a redirect to the login
    // page we need to do BOTH:
    //   1. POST /api/auth/logout — the server DELETEs the row in the
    //      `sessions` table and returns Set-Cookie with the same
    //      HttpOnly + Secure + SameSite=Strict + Path=/ attributes the
    //      login route used. A browser refresh mid-logout would
    //      resurrect the session if the row was not destroyed.
    //   2. Belt-and-suspenders JS cookie clear that writes the expiry
    //      into the past with matching attributes. The actual cookie
    //      name is `session_id` (set by /api/auth/login line 53); we
    //      also clear the `sm_session` name (no longer set by us, but
    //      kept here in case any browser extension or older tab has
    //      one lingering) and a non-HttpOnly name just in case.
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
      });
    } catch (e) {
      console.error('Logout request failed', e);
    }

    const past = 'Thu, 01 Jan 1970 00:00:00 UTC';
    const cookieSpecs = [
      'session_id=; expires=' + past + '; path=/; Secure; SameSite=Strict',
      'sm_session=; expires=' + past + '; path=/; Secure; SameSite=Strict',
    ];
    for (const c of cookieSpecs) document.cookie = c;

    setIsAuthenticated(false);

    // Hard-navigate to the root so the next render of the app sees
    // !isAuthenticated and mounts <Login />. No Access redirect — the
    // dashboard IS the login page.
    setTimeout(() => {
      window.location.replace('/');
    }, 50);
  }

  if (authLoading) return <div className="loading-screen">Initializing SEO Central...</div>;
  if (!isAuthenticated) return <Login onLoginSuccess={() => setIsAuthenticated(true)} />;

  return (
    <Router>
      <div className="app-layout">
        <Sidebar user={user} onLogout={handleLogout} />
        <main className="content">
          <ToastCenter />
          <Routes>
            <Route path="/" element={<DashboardHome />} />
            <Route path="/sites/:siteId" element={<SiteReport />} />
            <Route path="/profile" element={<Profile user={user} />} />
            {user?.role === 'superadmin' && <Route path="/team" element={<TeamSettings />} />}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    </Router>
  );
}


function ToastCenter() {
  const [toasts, setToasts] = useState([]);
  useEffect(() => {
    function onToast(e) {
      const toast = {
        id: crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()),
        type: e.detail?.type || 'info',
        message: e.detail?.message || '',
      };
      setToasts(prev => [...prev, toast].slice(-5));
      setTimeout(() => setToasts(prev => prev.filter(t => t.id !== toast.id)), 5000);
    }
    window.addEventListener('sm-toast', onToast);
    return () => window.removeEventListener('sm-toast', onToast);
  }, []);
  if (!toasts.length) return null;
  return <div className="toast-stack">
    {toasts.map(t => <div key={t.id} className={`toast ${t.type}`}>
      <span>{t.message}</span>
      <button type="button" aria-label="Close notification" onClick={() => setToasts(prev => prev.filter(x => x.id !== t.id))}>×</button>
    </div>)}
  </div>;
}

function DashboardHome() {
  const [sites, setSites] = useState([]);
  const [form, setForm] = useState({ name: '', domain: '', platform: 'wordpress', server_type: 'apache_litespeed', shopify_admin_domain: '' });
  const [message, setMessage] = useState('');
  const [busySiteId, setBusySiteId] = useState('');

  useEffect(() => { loadSites(); }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const shopifyError = params.get('shopify_error');
    if (shopifyError) {
      setMessage(`Shopify connection failed: ${shopifyError}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const stats = useMemo(() => ({
    sites: sites.length,
    connected: sites.filter(s => Number(s.bridge_enabled) === 1 || Number(s.connector_enabled) === 1).length,
    bridgeNeeded: sites.filter(s => !['static'].includes(s.platform)).length,
  }), [sites]);

  async function loadSites() {
    const res = await fetch('/api/sites');
    if (!res.ok) return;
    const data = await res.json();
    setSites(data.sites || []);
    window.dispatchEvent(new Event('sm-sites-updated'));
  }

  async function addSite(e) {
    e.preventDefault();
    setMessage('Saving site...');
    const res = await fetch('/api/sites', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form)
    });
    const data = await res.json();
    if (!res.ok) {
      setMessage(data.error || 'Failed to save site');
      return;
    }
    setMessage(form.platform === 'shopify' ? 'Shopify site added. Click Connect Shopify to authorize read-only audit access.' : 'Site added. Download the bridge file and upload it to the site root.');
    setForm({ name: '', domain: '', platform: 'wordpress', server_type: 'apache_litespeed', shopify_admin_domain: '' });
    await loadSites();
    window.dispatchEvent(new Event('sm-sites-updated'));
  }

  async function downloadBridge(site) {
    setBusySiteId(site.id);
    try {
      const res = await fetch(`/api/sites/${site.id}/bridge`);
      const text = await res.text();
      if (!res.ok) {
        setMessage(text || 'Unable to generate bridge file');
        return;
      }
      const blob = new Blob([text], { type: 'application/x-httpd-php' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'sm-seo-bridge.php';
      a.click();
      URL.revokeObjectURL(url);
      setMessage(`Bridge generated for ${site.name}. Upload sm-seo-bridge.php to the site root.`);
    } finally { setBusySiteId(''); }
  }

  async function testBridge(site) {
    setBusySiteId(site.id);
    const label = site.platform === 'shopify' ? 'Shopify connector' : 'bridge';
    setMessage(`Testing ${label} for ${site.name}...`);
    try {
      const res = await fetch(`/api/sites/${site.id}/ping`);
      const data = await res.json();
      if (!res.ok || !data.ok) setMessage(data.error || `${label} test failed`);
      else { setMessage(data.message || `${label} connected successfully`); await loadSites(); window.dispatchEvent(new Event('sm-sites-updated')); }
    } catch (e) { setMessage(`${label} test failed: ${e.message}`); }
    finally { setBusySiteId(''); }
  }

  function connectShopify(site) {
    setMessage(`Redirecting to Shopify to connect ${site.name}...`);
    window.location.href = `/api/shopify/install?site_id=${encodeURIComponent(site.id)}`;
  }


  return (
    <>
      {message && <div className="notice">{message}</div>}
      <section className="hero-card">
        <div>
          <h1><Activity size={26} /> SEO Central Dashboard</h1>
          <p>Central monitoring for SEO health, bridges, redirects, and performance.</p>
        </div>
        <div className="hero-stats">
          <div><strong>{stats.sites}</strong><span>Sites</span></div>
          <div><strong>{stats.connected}</strong><span>Connected</span></div>
          <div><strong>{stats.bridgeNeeded}</strong><span>Connector sites</span></div>
        </div>
      </section>

      <section className="grid">
        <div className="card">
          <h2><Plus size={20} /> Add Site</h2>
          <form onSubmit={addSite} className="form">
            <label>Site name</label>
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Sweetmag Digital" required />
            <label>Domain</label>
            <input value={form.domain} onChange={e => setForm({ ...form, domain: e.target.value })} placeholder="https://sweetmag.digital" required />
            <label>Platform</label>
            <select value={form.platform} onChange={e => setForm({ ...form, platform: e.target.value })}>{platforms.map(p => <option key={p} value={p}>{p}</option>)}</select>
            <label>Server type</label>
            <select value={form.server_type} onChange={e => setForm({ ...form, server_type: e.target.value })}>{serverTypes.map(s => <option key={s} value={s}>{s}</option>)}</select>
            {form.platform === 'shopify' && <div className="connector-fields">
              <label>Shopify admin domain</label>
              <input value={form.shopify_admin_domain} onChange={e => setForm({ ...form, shopify_admin_domain: e.target.value })} placeholder="your-store.myshopify.com" />
              <p className="muted tiny">Shopify uses OAuth. After adding the site, click <strong>Connect Shopify</strong> to authorize read-only audit access. No write scopes / auto-fix for Shopify.</p>
            </div>}
            <button type="submit">Add Site</button>
          </form>
        </div>

        <div className="card">
          <h2><ShieldCheck size={20} /> Setup Flow</h2>
          <ol className="setup-list">
            <li>Add a site and choose platform/server type.</li>
            <li>Download the generated bridge for PHP-based sites.</li>
            <li>Upload <code>sm-seo-bridge.php</code> to the site root.</li>
            <li>Click <strong>Test Bridge</strong>.</li>
            <li>Click <strong>Enter</strong> to open the site SEO monitor and run audits there.</li>
          </ol>
          <p>Shopify uses an Admin API connector instead of a PHP bridge. Static sites use manual/config exports.</p>
        </div>
      </section>

      <section className="card">
        <h2><Globe size={20} /> Sites</h2>
        <div className="site-list">
          {sites.length === 0 && <p className="muted">No sites added yet.</p>}
          {sites.map(site => {
            const needsBridge = !['shopify', 'static'].includes(site.platform);
            const connected = Number(site.bridge_enabled) === 1 || Number(site.connector_enabled) === 1;
            const isShopify = site.platform === 'shopify';
            return (
              <div className="site-row" key={site.id}>
                <div className="site-main">
                  <strong>{site.name}</strong>
                  <span>{site.domain}</span>
                  <small>{site.platform} · {site.server_type}</small>
                </div>
                <div className="site-status">
                  {isShopify ? <span className={connected ? 'pill ok' : 'pill warn'}>{connected ? 'Shopify read-only connected' : 'Shopify OAuth pending'}</span> : (needsBridge ? <span className={connected ? 'pill ok' : 'pill warn'}>{connected ? 'Bridge connected' : 'Bridge pending'}</span> : <span className="pill muted-pill">Manual/config connector</span>)}
                </div>
                <div className="actions">
                  <Link className="button secondary" to={`/sites/${site.id}`}><Eye size={16} /> Enter</Link>
                  {isShopify && !connected && <button disabled={busySiteId === site.id} onClick={() => connectShopify(site)}><PlugZap size={16} /> Connect Shopify</button>}
                  {needsBridge && <button disabled={busySiteId === site.id} onClick={() => downloadBridge(site)}><Download size={16} /> Bridge</button>}
                  {(needsBridge || isShopify) && <button disabled={busySiteId === site.id} onClick={() => testBridge(site)}><PlugZap size={16} /> Test</button>}
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </>
  );
}

function SiteReport() {
  const { siteId } = useParams();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [message, setMessage] = useState('');
  const [auditProgress, setAuditProgress] = useState(null);
  const [psiScores, setPsiScores] = useState({ mobile: null, desktop: null });

  // Per-resource pagination state. Each tab that paginates gets its own
  // page index; switching tabs does NOT reset it. The current page is
  // threaded into loadReport() so the response only contains the requested
  // slice, which keeps the report payload under ~50 KB even for 1000-page
  // sites.
  const [pagesPage, setPagesPage] = useState(1);
  const [issuesPage, setIssuesPage] = useState(1);
  const [brokenPage, setBrokenPage] = useState(1);
  const [contentPage, setContentPage] = useState(1);
  const [pageLoading, setPageLoading] = useState(false);
  const PAGE_LIMIT = 50;

  useEffect(() => {
    if (data?.site?.domain) {
      setPsiScores(getCachedPageSpeedScores(data.site.domain));
    }
  }, [data?.site?.domain, tab]);

  async function loadReport(opts = {}) {
    const isPagination = opts.pagination === true;
    if (isPagination) setPageLoading(true); else setLoading(true);
    const params = new URLSearchParams();
    params.set('pages_page', String(opts.pagesPage ?? pagesPage));
    params.set('pages_limit', String(opts.pagesLimit ?? PAGE_LIMIT));
    params.set('issues_page', String(opts.issuesPage ?? issuesPage));
    params.set('issues_limit', String(opts.issuesLimit ?? PAGE_LIMIT));
    params.set('broken_page', String(opts.brokenPage ?? brokenPage));
    params.set('broken_limit', String(opts.brokenLimit ?? PAGE_LIMIT));
    params.set('content_page', String(opts.contentPage ?? contentPage));
    params.set('content_limit', String(opts.contentLimit ?? PAGE_LIMIT));
    const res = await fetch(`/api/sites/${siteId}/report?${params.toString()}`);
    const json = await res.json();
    setData(json);
    if (isPagination) setPageLoading(false); else setLoading(false);
  }

  useEffect(() => { loadReport(); }, [siteId]);

  // Re-fetch the report (lightweight — only the requested slice changes)
  // when any paginated page index changes. The on-screen data updates in
  // place so the user never sees the full-page skeleton flash.
  useEffect(() => {
    if (!data) return;
    loadReport({ pagination: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagesPage, issuesPage, brokenPage, contentPage]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('shopify_connected') === '1') {
      notify('Shopify connected successfully. You can run a read-only audit now.', 'success');
      window.history.replaceState({}, '', `/sites/${siteId}`);
    }
  }, [siteId]);

  async function runAudit() {
    setRunning(true);
    notify('Running audit. Progress will continue in the audit window.', 'success');
    const logs = [];
    const pushLog = (line) => {
      logs.push(line);
      setAuditProgress(prev => ({
        pct: prev?.pct || 5,
        step: line,
        logs: [...logs].slice(-80),
      }));
    };

    try {
      pushLog('Starting audit job');
      let res = await fetch(`/api/sites/${siteId}/audit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ start: true }),
      });
      let json = await safeJson(res);
      if (!res.ok || !json.ok) throw new Error(json.error || json.message || 'Unable to start audit');

      let auditId = json.audit_id;
      let cursor = json.cursor || 0;
      let total = json.total || 0;
      setAuditProgress({ pct: json.pct || 5, step: json.message || 'Audit initialized', logs: [...logs, json.message || 'Audit initialized'] });
      logs.push(json.message || 'Audit initialized');

      let done = false;
      while (!done) {
        let batchAttempt = 0;
        while (true) {
          try {
            res = await fetch(`/api/sites/${siteId}/audit`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ audit_id: auditId, cursor, batch_size: 8 }),
            });
            json = await safeJson(res);
            if (!res.ok || !json.ok) throw new Error(json.error || json.message || 'Audit batch failed');
            break;
          } catch (err) {
            batchAttempt += 1;
            const retryable = batchAttempt < 3;
            logs.push(`Batch retry ${batchAttempt}/2 after temporary API response issue: ${err.message || err}`);
            setAuditProgress({ pct: Math.max(5, Math.round((cursor / Math.max(1, total)) * 94)), step: 'Retrying audit batch...', logs: [...logs].slice(-80) });
            if (!retryable) throw err;
            await sleep(1000 * batchAttempt);
          }
        }
        done = !!json.done;
        cursor = json.cursor || cursor;
        total = json.total || total;
        const pct = json.pct || (done ? 100 : Math.max(5, Math.round((cursor / Math.max(1, total)) * 94)));
        const msg = json.message || (done ? 'Audit completed' : `Crawled ${cursor}/${total} URLs`);
        logs.push(msg);
        setAuditProgress({ pct, step: msg, logs: [...logs].slice(-80) });
        await new Promise(resolve => setTimeout(resolve, 160));
      }

      setAuditProgress(prev => ({ pct: 100, step: 'Audit completed', logs: [...(prev?.logs || []), 'Audit completed'] }));
      notify(json.message || 'Audit completed.', 'success');
      // Reset to first page of each paginated resource so the user lands
      // on the latest top-priority issues / worst-scoring pages / fresh
      // broken links after a re-audit.
      setPagesPage(1);
      setIssuesPage(1);
      setBrokenPage(1);
      setContentPage(1);
      await loadReport({ pagesPage: 1, issuesPage: 1, brokenPage: 1, contentPage: 1 });
      setTimeout(() => setAuditProgress(null), 900);
    } catch (e) {
      const msg = e.message || String(e);
      setAuditProgress(prev => ({ pct: 100, step: 'Audit failed', logs: [...(prev?.logs || []), `ERROR: ${msg}`] }));
      notify(`Audit failed: ${msg}`, 'error');
    } finally {
      setRunning(false);
    }
  }


  async function fixIssue(issue) {
    notify(`Applying fix for ${issue.issue_type}...`, 'info');
    try {
      const res = await fetch(`/api/sites/${siteId}/fix`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ issue_id: issue.id, method: 'apply' }),
      });
      const json = await res.json();
      if (!res.ok || !json.ok) {
        notify(json.error || 'Auto-fix failed', 'error');
        return;
      }
      notify(json.message || 'Fix applied. Run audit again to verify.', 'success');
      await loadReport();
    } catch (e) {
      notify(`Auto-fix failed: ${e.message}`, 'error');
    }
  }

  async function revertIssue(issue) {
    // Two-step confirmation: the Revert button restores WordPress meta
    // to its pre-fix state. It is safe (the bridge only writes to
    // _previous-backed-up keys, the value is the captured pre-fix
    // value, and the original primary meta key is exactly what was
    // there before the fix), but it is a real production change and
    // should require an explicit confirm.
    if (!window.confirm(`Revert the auto-fix for this ${issue.issue_type} issue on ${issue.page_url || 'this site'}? The WordPress meta will be restored to its pre-fix value.`)) return;
    notify(`Reverting ${issue.issue_type} fix...`, 'info');
    try {
      const res = await fetch(`/api/sites/${siteId}/fix`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ issue_id: issue.id, method: 'rollback' }),
      });
      const json = await res.json();
      if (!res.ok || !json.ok) {
        notify(json.error || 'Revert failed', 'error');
        return;
      }
      notify(json.message || 'Fix reverted. Run audit again to verify.', 'success');
      await loadReport();
    } catch (e) {
      notify(`Revert failed: ${e.message}`, 'error');
    }
  }

  // Bug #3 fix: Verify Fix is a single-URL on-demand re-audit. The
  // teammate applies a one-click fix, then clicks Verify Fix instead
  // of running a full 165+ page audit just to see if that one page is
  // clean. The backend re-fetches only that page (with cache-busting
  // headers, see Bug #1) and updates the issue status directly: if
  // the rule no longer fires on the fresh HTML, the issue is moved to
  // 'fixed_verified' and disappears from the Actions tab. If the rule
  // still fires, it is moved back to 'open' with a notification so
  // the teammate knows to investigate.
  async function verifyIssue(issue) {
    if (!issue?.page_url) {
      notify('This issue has no associated page URL (sitewide issue). Run a full audit instead.', 'error');
      return;
    }
    notify(`Verifying fix on ${issue.page_url}...`, 'info');
    try {
      const res = await fetch(`/api/sites/${siteId}/verify-page`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ issue_id: issue.id, page_url: issue.page_url }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        notify(data.error || 'Verification failed', 'error');
        await loadReport();
        return;
      }
      if (data.verified) {
        notify(data.message || 'Fix verified. Cleared from the actions list.', 'success');
      } else if (data.still_broken) {
        notify(data.message || 'The issue still fires on the live page.', 'error');
      } else {
        notify(data.message || `Re-fetched page (HTTP ${data.http_status}, score ${data.score}).`, 'info');
      }
      await loadReport();
    } catch (e) {
      notify(`Verification failed: ${e.message}`, 'error');
    }
  }

  if (loading) return <div className="loading-panel">Loading report...</div>;
  if (!data?.site) return <div className="notice error">Site not found.</div>;

  const summary = data.summary;
  const site = data.site;
  const issues = data.issues || [];
  const issueGroups = summary?.issue_groups || [];
  const critical = issues.filter(i => i.severity === 'critical');
  const warnings = issues.filter(i => i.severity === 'warning');
  const infos = issues.filter(i => i.severity === 'info');
  const criticalGroups = issueGroups.filter(i => i.severity === 'critical');
  const warningGroups = issueGroups.filter(i => i.severity === 'warning');
  const score = summary?.overall_score ?? data.latest_run?.overall_score ?? 0;
  const health = score >= 80 ? 'Good' : score >= 60 ? 'Needs Work' : 'Critical';

  function handlePageSpeedResult(nextStrategy, result) {
    setPsiScores(prev => ({ ...prev, [nextStrategy]: result?.score ?? null }));
  }

  return (
    <>
      {auditProgress && <AuditProgressOverlay progress={auditProgress} onClose={() => setAuditProgress(null)} />}
      <div className="report-topbar">
        <Link to="/" className="back-link"><ArrowLeft size={16} /> Back to sites</Link>
        <button onClick={runAudit} disabled={running}><RefreshCw size={16} /> {running ? 'Auditing...' : 'Run Audit'}</button>
      </div>

      {!summary ? (
        <section className="hero-card report-hero">
          <div>
            <h1><Activity size={26} /> {site.name}</h1>
            <p>No audit result yet. Click <strong>Run Audit</strong> to generate the first SEO monitor report.</p>
          </div>
        </section>
      ) : (
        <>
          <section className="hero-card report-hero">
            <div className={`score-ring ${scoreClass(score)}`}>{score}<span>Score</span></div>
            <div className="report-title">
              <h1>Site Health: {health}</h1>
              <p>{site.domain} · {summary.pages_ok}/{summary.pages_total} pages OK · {criticalGroups.length} critical groups · {warningGroups.length} warning groups · {data.broken_links_count ?? summary.broken_links_count ?? 0} broken links</p>
            </div>
            <div className="hero-stats">
              <div><strong>{criticalGroups.length}</strong><span>Critical Groups</span></div>
              <div><strong>{warningGroups.length}</strong><span>Warning Groups</span></div>
              <div><strong>{psiScores.mobile ?? '-'}</strong><span>Mobile PSI</span></div>
              <div><strong>{psiScores.desktop ?? '-'}</strong><span>Desktop PSI</span></div>
            </div>
          </section>

          <div className="tabs">
            {[
              ['overview', 'Overview'],
              ['actions', 'Actions'],
              ['pages', 'Pages'],
              ['indexability', 'Indexability'],
              ['crawlability', 'Crawlability'],
              ['broken', 'Broken Links'],
              ['redirects', 'Redirects'],
              ['content', 'Content'],
              ['schema', 'Schema'],
              ['technical', 'Technical'],
              ['pagespeed', 'PageSpeed'],
              ['history', 'History'],
              ['help', 'Help'],
            ].map(([key, label]) => <button key={key} onClick={() => setTab(key)} className={tab === key ? 'active' : ''}>{label}</button>)}
          </div>

          {tab === 'overview' && <OverviewTab summary={summary} issueGroups={issueGroups} />}
          {tab === 'actions' && <ActionsTab issues={data.issues || []} issuesMeta={data.issues_meta} issueGroups={issueGroups} onPageChange={setIssuesPage} onFixIssue={fixIssue} onRevertIssue={revertIssue} onVerifyIssue={verifyIssue} pageLoading={pageLoading} />}
          {tab === 'pages' && <PagesTab pages={data.pages || []} pagesMeta={data.pages_meta} onPageChange={setPagesPage} pageLoading={pageLoading} />}
          {tab === 'indexability' && <CategoryTab title="Indexability" category="indexability" issueGroups={issueGroups} />}
          {tab === 'crawlability' && <CrawlabilityTab summary={summary} issueGroups={issueGroups} />}
          {tab === 'broken' && <BrokenLinksTab siteId={siteId} initialBrokenLinks={data.broken_links || []} initialBrokenMeta={data.broken_links_meta} initialJob={data.latest_broken_link_job || null} onPageChange={setBrokenPage} pageLoading={pageLoading} />}
          {tab === 'redirects' && <RedirectsTab siteId={siteId} siteDomain={site.domain} />}
          {tab === 'content' && <ContentTab title="Content SEO" category="content" issueGroups={issueGroups} contentIssues={data.content_issues || []} contentMeta={data.content_issues_meta} onPageChange={setContentPage} pageLoading={pageLoading} />}
          {tab === 'schema' && <SchemaTab summary={summary} issueGroups={issueGroups} />}
          {tab === 'technical' && <TechnicalTab summary={summary} issueGroups={issueGroups} />}
          {tab === 'pagespeed' && <PageSpeedTab pagespeed={summary.pagespeed} site={site} onResult={handlePageSpeedResult} />}
          {tab === 'history' && <HistoryTab history={data.history || []} />}
          {tab === 'help' && <section className="card"><h2><BookOpen size={20} /> Operator Runbook</h2><Runbook /></section>}
        </>
      )}
    </>
  );
}


function AuditProgressOverlay({ progress, onClose }) {
  return <div className="audit-modal-backdrop"><div className="audit-progress-card audit-modal">
    <div className="audit-progress-head">
      <strong>Audit running</strong>
      <span>{progress.pct}%</span>
      <button type="button" className="modal-close" onClick={onClose}>×</button>
    </div>
    <div className="audit-progress-bar"><div style={{ width: `${progress.pct}%` }} /></div>
    <p>{progress.step}</p>
    <pre>{(progress.logs || []).map((l, i) => `$ ${i === 0 ? 'start' : 'step'}: ${l}`).join('\n')}</pre>
  </div></div>;
}

function OverviewTab({ summary, issueGroups }) {
  // The six lowest-scoring pages are pre-computed in the audit's light
  // summary (audit.ts:buildLightSummary). We use that instead of sorting
  // summary.pages, because the report endpoint now strips summary.pages
  // from the response to keep the payload small.
  const topPages = summary.lowest_score_pages || [];
  const priorities = summary.top_priorities || issueGroups.slice(0, 8);
  return <section className="stack">
    <SectionScoreGrid scores={summary.section_scores || {}} />
    <section className="grid report-grid">
      <div className="card"><h2><AlertTriangle size={20} /> Top Priorities</h2><IssueGroupList groups={priorities} /></div>
      <div className="card"><h2><FileText size={20} /> Lowest Page Scores</h2><PageMiniList pages={topPages} /></div>
    </section>
  </section>;
}

function ActionsTab({ issues, issuesMeta, issueGroups, onPageChange, onFixIssue, onRevertIssue, onVerifyIssue, pageLoading }) {
  const auto = issues.filter(i => Number(i.auto_fixable) === 1);
  const manualGroups = issueGroups.filter(i => Number(i.auto_fixable) !== 1);
  return <section className="stack">
    <div className="card">
      <h2><Zap size={20} /> Safe Auto-fix ({auto.length}{issuesMeta && issuesMeta.total > auto.length ? ` of ${issuesMeta.total}` : ''})</h2>
      <p className="muted">Only safe bridge actions appear here, such as robots.txt and redirect/config fixes. Content and layout changes stay manual until a CMS preview workflow is added. If an auto-fix has already been applied, a <strong>Revert</strong> button appears for issue types that captured a backup (currently canonical) and a <strong>Verify Fix</strong> button re-fetches only that page to confirm the fix took effect — no full re-audit needed.</p>
      <IssueList issues={auto} showFix onFixIssue={onFixIssue} onRevertIssue={onRevertIssue} onVerifyIssue={onVerifyIssue} />
      <Pagination meta={issuesMeta} onPageChange={onPageChange} loading={pageLoading} />
    </div>
    <div className="card">
      <h2><Wrench size={20} /> Manual Fix Groups ({manualGroups.length})</h2>
      <p className="muted">Grouped by issue pattern so template-level problems are easier to fix once instead of reviewing hundreds of repeated rows.</p>
      <IssueGroupList groups={manualGroups} />
    </div>
  </section>;
}

function PagesTab({ pages, pagesMeta, onPageChange, pageLoading }) {
  return <section className="card">
    <h2><FileText size={20} /> Pages</h2>
    {!pages?.length && <p className="muted">No pages crawled yet.</p>}
    {pages?.length > 0 && <div className="responsive-table"><table>
      <thead><tr><th>Page</th><th>HTTP</th><th>Score</th><th>Indexable</th><th>Title</th><th>Meta</th><th>H1</th><th>Schema</th><th>Alt</th><th>Links</th><th>Issues</th></tr></thead>
      <tbody>{pages.map(p => <tr key={p.url}><td><a href={p.url} target="_blank">{p.label}</a><small>{p.url}</small></td><td>{p.http_status}</td><td><span className={`score-badge ${scoreClass(p.score)}`}>{p.score}</span></td><td>{p.indexable === false ? <span className="status-bad">No</span> : <span className="status-ok">Yes</span>}</td><td>{p.title_len}</td><td>{p.meta_desc_len}</td><td>{p.h1_count}</td><td>{p.schema_count}</td><td>{Math.max(0, (p.img_total || 0) - (p.img_no_alt || 0))}/{p.img_total || 0}</td><td>{p.internal_links || 0} / {p.external_links || 0}</td><td>{p.issues?.length ?? p.issue_count ?? 0}</td></tr>)}</tbody>
    </table></div>}
    <Pagination meta={pagesMeta} onPageChange={onPageChange} loading={pageLoading} />
  </section>;
}

function TechnicalTab({ summary, issueGroups }) {
  const technicalGroups = issueGroups.filter(g => ['technical', 'links'].includes(g.category));
  return <section className="stack">
    <section className="grid report-grid">
      <div className="card"><h2>robots.txt</h2><StatusLine ok={summary.robots?.ok && !summary.robots?.blocks_all} label={summary.robots?.blocks_all ? 'Blocking crawlers' : summary.robots?.ok ? 'Accessible' : 'Not accessible'} /><pre>{summary.robots?.preview || 'No preview available'}</pre></div>
      <div className="card"><h2>sitemap.xml</h2><StatusLine ok={summary.sitemap?.ok} label={summary.sitemap?.ok ? `${summary.sitemap.url_count} URLs discovered` : 'Sitemap not found'} /><p>Checked: {(summary.sitemap?.checked || []).join(', ')}</p><p>Found: {(summary.sitemap?.found || []).join(', ') || 'None'}</p></div>
    </section>
    <div className="card"><h2>Technical Issue Groups</h2><IssueGroupList groups={technicalGroups} /></div>
  </section>;
}

function CategoryTab({ title, category, issueGroups, pages }) {
  const groups = issueGroups.filter(g => g.category === category);
  const affected = groups.reduce((sum, g) => sum + (g.affected_count || 0), 0);
  return <section className="stack">
    <div className="card">
      <h2>{title}</h2>
      <p className="muted">{groups.length} issue groups · {affected} affected findings</p>
      <IssueGroupList groups={groups} />
    </div>
  </section>;
}

// ContentTab is CategoryTab's paginated sibling. It still shows the
// high-level issue groups (template-level signals) at the top, then
// renders the underlying per-page issues server-side paginated so the
// browser only ever holds 50 content-category rows at a time, no
// matter how many pages the audit crawled. Without this, a 1000-page
// audit with 70% title issues would ship ~700 rows of content
// issues to the dashboard on every Content-tab open.
function ContentTab({ title, category, issueGroups, contentIssues, contentMeta, onPageChange, pageLoading }) {
  const groups = issueGroups.filter(g => g.category === category);
  const affected = groups.reduce((sum, g) => sum + (g.affected_count || 0), 0);
  return <section className="stack">
    <div className="card">
      <h2>{title}</h2>
      <p className="muted">{groups.length} issue groups · {affected} affected findings</p>
      <IssueGroupList groups={groups} />
    </div>
    <div className="card">
      <h2>Per-page content findings</h2>
      <p className="muted">Individual title, meta description, H1, image alt, and word-count issues, paginated {contentMeta ? `(${contentMeta.page} of ${contentMeta.total_pages})` : ''}.</p>
      <IssueList issues={contentIssues} />
      <Pagination meta={contentMeta} onPageChange={onPageChange} loading={pageLoading} />
    </div>
  </section>;
}

function CrawlabilityTab({ summary, issueGroups }) {
  const groups = issueGroups.filter(g => g.category === 'crawlability');
  return <section className="stack">
    <div className="card">
      <h2>Crawlability</h2>
      <div className="metric-grid">
        <Metric label="Pages crawled" value={summary.pages_total} />
        <Metric label="Pages OK" value={summary.pages_ok} />
        <Metric label="Sitemap URLs" value={summary.sitemap?.url_count || 0} />
        <Metric label="Broken links" value={summary.broken_links_count || 0} />
      </div>
    </div>
    <div className="card"><h2>Crawlability Issue Groups</h2><IssueGroupList groups={groups} /></div>
  </section>;
}

function BrokenLinksTab({ siteId, initialBrokenLinks, initialBrokenMeta, initialJob, onPageChange, pageLoading }) {
  const [job, setJob] = useState(initialJob || null);
  const [brokenLinks, setBrokenLinks] = useState(initialBrokenLinks || []);
  const [brokenMeta, setBrokenMeta] = useState(initialBrokenMeta || null);
  const [scanning, setScanning] = useState(false);
  const [progress, setProgress] = useState(null);
  const [scanMessage, setScanMessage] = useState('');

  useEffect(() => {
    setBrokenLinks(initialBrokenLinks || []);
    setBrokenMeta(initialBrokenMeta || null);
    setJob(initialJob || null);
  }, [initialBrokenLinks, initialBrokenMeta, initialJob]);

  async function runScan() {
    setScanning(true);
    setScanMessage('');
    const logs = [];
    const pushLog = (line) => {
      logs.push(line);
      setProgress(prev => ({ pct: prev?.pct || 3, step: line, logs: [...logs].slice(-60) }));
    };

    try {
      pushLog('Starting broken-link scan');
      let res = await fetch(`/api/sites/${siteId}/broken-links`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      let json = await safeJson(res);
      if (!res.ok || !json.ok) throw new Error(json.error || 'Unable to start scan');

      let jobId = json.job_id;
      let cursor = json.cursor || 0;
      let total = json.total || 0;
      setJob({ id: jobId, status: 'running', total_links: total, checked_links: 0, broken_count: 0 });
      setProgress({ pct: json.pct || 5, step: json.message || 'Scan initialized', logs });
      logs.push(json.message || 'Scan initialized');

      let done = false;
      let liveBroken = 0;
      while (!done) {
        let attempt = 0;
        while (true) {
          try {
            res = await fetch(`/api/sites/${siteId}/broken-links`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ job_id: jobId, cursor, batch_size: 5 }),
            });
            json = await safeJson(res);
            if (!res.ok || !json.ok) throw new Error(json.error || 'Batch failed');
            break;
          } catch (err) {
            attempt += 1;
            if (attempt >= 3) throw err;
            await sleep(900 * attempt);
          }
        }
        done = !!json.done;
        cursor = json.cursor || cursor;
        total = json.total || total;
        if (typeof json.batch_broken === 'number') liveBroken += json.batch_broken;
        const pct = json.pct || (done ? 100 : Math.max(5, Math.round((cursor / Math.max(1, total)) * 94)));
        const msg = json.message || (done ? 'Scan completed' : `Checked ${cursor}/${total} links`);
        logs.push(msg);
        setProgress({ pct, step: msg, logs: [...logs].slice(-60) });
        setJob(prev => prev ? { ...prev, checked_links: cursor, broken_count: liveBroken } : prev);
        await new Promise(r => setTimeout(r, 200));
      }

      // Pull the first page of broken links from the dedicated endpoint.
      // The full table is paginated at ?page=N&limit=50; subsequent pages
      // are loaded by the <Pagination> control below.
      const finalRes = await fetch(`/api/sites/${siteId}/broken-links?job_id=${encodeURIComponent(jobId)}&page=1&limit=50`);
      const finalData = await safeJson(finalRes);
      if (finalRes.ok) {
        setJob(finalData.job);
        setBrokenLinks(finalData.broken || []);
        setBrokenMeta(finalData.broken_meta || { total: finalData.broken?.length || 0, page: 1, limit: 50, total_pages: 1 });
        setScanMessage(`${finalData.summary?.broken_count ?? finalData.broken?.length ?? 0} broken link(s) found across ${finalData.summary?.total_links || 0} checked.`);
        // Reset the parent-level page state so the report re-fetch (if it
        // happens later) lands on the first page of the fresh data.
        if (onPageChange) onPageChange(1);
      }
      setProgress(prev => ({ pct: 100, step: 'Scan completed', logs: [...(prev?.logs || []), 'Scan completed'] }));
      setTimeout(() => setProgress(null), 1200);
    } catch (e) {
      const msg = e.message || String(e);
      setProgress(prev => ({ pct: 100, step: 'Scan failed', logs: [...(prev?.logs || []), `ERROR: ${msg}`] }));
      setScanMessage(`Broken-link scan failed: ${msg}`);
    } finally {
      setScanning(false);
    }
  }

  const scanComplete = job?.status === 'completed';
  const hasResults = Array.isArray(brokenLinks) && brokenLinks.length > 0;

  return <section className="stack">
    <div className="card broken-link-toolbar">
      <div>
        <h2><AlertTriangle size={20} /> Broken Link Audit</h2>
        <p className="muted">
          {job
            ? <>Last scan: <strong>{job.status}</strong> · {job.checked_links || 0}/{job.total_links || 0} links checked · {job.broken_count || 0} broken{job.started_at ? ` · ${formatDate(job.started_at)}` : ''}</>
            : 'No broken-link scan has been run yet for this site.'}
        </p>
      </div>
      <button onClick={runScan} disabled={scanning}>
        {scanning ? 'Scanning...' : (job ? 'Re-run Scan' : 'Run Scan')}
      </button>
    </div>

    {progress && <div className="card broken-link-progress">
      <div className="audit-progress-head">
        <strong>Broken-link scan in progress</strong>
        <span>{progress.pct}%</span>
      </div>
      <div className="audit-progress-bar"><div style={{ width: `${progress.pct}%` }} /></div>
      <p>{progress.step}</p>
    </div>}

    {scanMessage && !progress && <div className="notice">{scanMessage}</div>}

    <div className="card">
      <h2>Broken Links</h2>
      {!job && !scanning && <p className="muted">Click <strong>Run Scan</strong> to HEAD-check every internal link found in the latest SEO audit. Requires at least one completed audit to discover pages.</p>}
      {job && !scanComplete && !hasResults && !scanning && <p className="muted">Scan {job.status}. Results will appear here when complete.</p>}
      {scanComplete && !hasResults && <p className="status-ok">No broken internal links found across {job.total_links} checked links.</p>}
      {hasResults && <div className="responsive-table"><table>
        <thead><tr><th>Found On</th><th>Broken URL</th><th>Status</th><th>Method</th></tr></thead>
        <tbody>{brokenLinks.map((b, idx) => <tr key={`${b.link_url}-${idx}`}>
          <td>{b.source_page_url ? <a href={b.source_page_url} target="_blank" rel="noreferrer">{b.source_page_url}</a> : '-'}</td>
          <td><a href={b.link_url} target="_blank" rel="noreferrer">{b.link_url}</a></td>
          <td>{b.status_code || b.status || 0}</td>
          <td>{b.method || 'HEAD'}</td>
        </tr>)}</tbody>
      </table></div>}
      {scanComplete && <Pagination meta={brokenMeta} onPageChange={loadBrokenPage} loading={pageLoading || scanning} />}
    </div>
  </section>;

  async function loadBrokenPage(page) {
    const jobId = job?.id;
    if (!jobId) return;
    const url = `/api/sites/${siteId}/broken-links?job_id=${encodeURIComponent(jobId)}&page=${page}&limit=50`;
    const res = await fetch(url);
    const data = await safeJson(res);
    if (res.ok) {
      setBrokenLinks(data.broken || []);
      setBrokenMeta(data.broken_meta || null);
    }
  }
}

function SchemaTab({ summary, issueGroups }) {
  const groups = issueGroups.filter(g => g.category === 'schema');
  // pages_with_schema is pre-computed in the audit's light summary; we
  // can't compute it from summary.pages here because the report endpoint
  // strips that array from the response.
  const withSchema = summary.pages_with_schema ?? 0;
  return <section className="stack">
    <div className="card">
      <h2>Schema / Structured Data</h2>
      <div className="metric-grid">
        <Metric label="Pages with schema" value={`${withSchema}/${summary.pages_total || 0}`} />
        <Metric label="Schema issue groups" value={groups.length} />
      </div>
    </div>
    <div className="card"><h2>Schema Issue Groups</h2><IssueGroupList groups={groups} /></div>
  </section>;
}





function getCachedPageSpeedScores(domain) {
  if (!domain) return { mobile: null, desktop: null };
  const out = { mobile: null, desktop: null };
  for (const strategy of ['mobile', 'desktop']) {
    try {
      const cached = localStorage.getItem(`psi:${domain}:${strategy}`);
      if (!cached) continue;
      const parsed = JSON.parse(cached);
      if (Date.now() - parsed.ts < 1000 * 60 * 60) {
        out[strategy] = parsed.data?.score ?? null;
      }
    } catch {}
  }
  return out;
}

function PageSpeedTab({ pagespeed, site, onResult }) {
  const [results, setResults] = useState({ mobile: pagespeed?.mobile || null, desktop: pagespeed?.desktop || null });
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState({ mobile: false, desktop: false });
  const domain = site?.domain || '';

  useEffect(() => {
    if (!domain) return;
    const cachedScores = { mobile: null, desktop: null };
    for (const nextStrategy of ['mobile', 'desktop']) {
      try {
        const cached = localStorage.getItem(`psi:${domain}:${nextStrategy}`);
        if (!cached) continue;
        const parsed = JSON.parse(cached);
        if (Date.now() - parsed.ts < 1000 * 60 * 60) cachedScores[nextStrategy] = parsed.data;
      } catch {}
    }
    if (cachedScores.mobile || cachedScores.desktop) {
      setResults(prev => ({
        ...prev,
        ...(cachedScores.mobile ? { mobile: cachedScores.mobile } : {}),
        ...(cachedScores.desktop ? { desktop: cachedScores.desktop } : {}),
      }));
      if (cachedScores.mobile) onResult?.('mobile', cachedScores.mobile);
      if (cachedScores.desktop) onResult?.('desktop', cachedScores.desktop);
      
    }
  }, [domain]);

  async function getKey() {
    try {
      const res = await fetch('/api/config/pagespeed');
      const data = await res.json();
      return data.key || '';
    } catch { return ''; }
  }

  async function runBrowserPageSpeed(nextStrategy) {
    if (!domain) {
      setStatus('Site domain is missing, unable to run PageSpeed.');
      return;
    }
    setLoading(prev => ({ ...prev, [nextStrategy]: true }));
    setStatus(`Running ${nextStrategy === 'mobile' ? 'Mobile' : 'Desktop'} PageSpeed test from your browser...`);
    try {
      const key = await getKey();
      let api = `https://www.googleapis.com/pagespeedonline/v5/runPagespeed?url=${encodeURIComponent(domain + '/')}&strategy=${nextStrategy}&category=performance`;
      if (key) api += `&key=${encodeURIComponent(key)}`;
      const res = await fetch(api);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error?.message || `HTTP ${res.status}`);
      const parsed = parsePageSpeed(data);
      localStorage.setItem(`psi:${domain}:${nextStrategy}`, JSON.stringify({ ts: Date.now(), data: parsed }));
      setResults(prev => ({ ...prev, [nextStrategy]: parsed }));
      onResult?.(nextStrategy, parsed);
      notify(`${nextStrategy === 'mobile' ? 'Mobile' : 'Desktop'} PageSpeed test completed.`, 'success');
    } catch (e) {
      notify(formatPageSpeedError(e.message), 'error');
    } finally {
      setLoading(prev => ({ ...prev, [nextStrategy]: false }));
    }
  }

  return <section className="stack">
    <div className="card pagespeed-toolbar single-row">
      <div>
        <h2><Gauge size={20} /> PageSpeed Insights</h2>
        <p>Run Mobile and Desktop tests manually. Results are cached in your browser for 1 hour.</p>
      </div>
    </div>
    <div className="grid report-grid pagespeed-results-grid">
      <SpeedResultPanel title="Mobile" strategy="mobile" data={results.mobile} loading={loading.mobile} onRun={() => runBrowserPageSpeed('mobile')} error={pagespeed?.error} />
      <SpeedResultPanel title="Desktop" strategy="desktop" data={results.desktop} loading={loading.desktop} onRun={() => runBrowserPageSpeed('desktop')} error={pagespeed?.error} />
    </div>
  </section>;
}

function SpeedResultPanel({ title, strategy, data, loading, onRun, error }) {
  return <div className="card pagespeed-card speed-result-panel">
    <div className="speed-panel-head">
      <h2><Gauge size={20} /> {title} PageSpeed</h2>
      <button onClick={onRun} disabled={loading}>{loading ? `Running ${title}...` : 'Run'}</button>
    </div>
    {!data
      ? <p className="muted">No {strategy} PageSpeed result yet. Click Run to test from your browser. {error ? `Latest note: ${error}` : ''}</p>
      : <>
        <div className={`big-score ${scoreClass(data.score)}`}>{data.score ?? '-'}<span>Performance</span></div>
        <div className="metric-grid"><Metric label="FCP" value={data.fcp} /><Metric label="LCP" value={data.lcp} /><Metric label="TBT" value={data.tbt} /><Metric label="CLS" value={data.cls} /><Metric label="Speed Index" value={data.speed_index} /></div>
        <h3>Top Opportunities</h3><AuditFindingList items={data.opportunities || []} empty="No major opportunities returned." />
        <h3>Diagnostics</h3><AuditFindingList items={data.diagnostics || []} empty="No major diagnostics returned." />
      </>}
  </div>;
}

function parsePageSpeed(data) {
  const audits = data?.lighthouseResult?.audits || {};
  const scoreRaw = data?.lighthouseResult?.categories?.performance?.score;
  const opportunityList = Object.values(audits)
    .filter(a => a?.details?.type === 'opportunity' && a?.score !== 1)
    .slice(0, 8)
    .map(a => ({ title: a.title, displayValue: a.displayValue || '', description: stripMiniHtml(a.description || '') }));
  const diagnosticList = Object.values(audits)
    .filter(a => a?.scoreDisplayMode !== 'notApplicable' && a?.score !== null && typeof a?.score === 'number' && a.score < 0.9 && a?.details?.type !== 'opportunity')
    .slice(0, 8)
    .map(a => ({ title: a.title, displayValue: a.displayValue || '', description: stripMiniHtml(a.description || '') }));
  return {
    score: typeof scoreRaw === 'number' ? Math.round(scoreRaw * 100) : null,
    fcp: audits['first-contentful-paint']?.displayValue || null,
    lcp: audits['largest-contentful-paint']?.displayValue || null,
    cls: audits['cumulative-layout-shift']?.displayValue || null,
    tbt: audits['total-blocking-time']?.displayValue || null,
    speed_index: audits['speed-index']?.displayValue || null,
    opportunities: opportunityList,
    diagnostics: diagnosticList,
    tested_at: new Date().toISOString(),
  };
}

function stripMiniHtml(value) {
  return String(value || '').replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
}


function formatPageSpeedError(message) {
  const msg = String(message || 'Unknown PageSpeed error');
  if (/quota|Queries per day|Queries/i.test(msg)) {
    return 'PageSpeed quota exceeded. Add PAGESPEED_API_KEY in Cloudflare Pages environment variables, then restrict the key to this dashboard domain in GCP.';
  }
  if (/API key|key not valid|forbidden|403/i.test(msg)) {
    return 'PageSpeed API key issue. Check PAGESPEED_API_KEY in Cloudflare Pages and the GCP key referrer restriction.';
  }
  return `PageSpeed failed: ${msg}`;
}


function SectionScoreGrid({ scores }) {
  const items = [
    ['Technical', scores.technical],
    ['Indexability', scores.indexability],
    ['Content', scores.content],
    ['Crawlability', scores.crawlability],
    ['Schema', scores.schema],
    ['Links', scores.links],
  ];
  return <div className="score-grid">{items.map(([label, value]) => <div key={label} className="score-tile"><strong className={scoreClass(value)}>{Number.isFinite(Number(value)) ? value : '-'}</strong><span>{label}</span></div>)}</div>;
}

function IssueGroupList({ groups }) {
  if (!groups?.length) return <p className="muted">No issue groups found here.</p>;
  return <div className="issue-list grouped">{groups.map((g, idx) => {
    const meta = issueTypeMeta(g.issue_type);
    const { Icon } = meta;
    return <div className="issue-card group-card" key={g.key || idx}>
      <span className={`dot ${g.severity}`}></span>
      <div>
        <div className="issue-meta">
          <span className={`severity ${g.severity}`}>{g.severity}</span>
          <span>{g.category || 'technical'}</span>
          <span className="issue-type"><Icon size={14} /> {meta.label}</span>
          <span className="pill muted-pill">{g.affected_count || 0} affected</span>
          {g.auto_fixable ? <span className="pill ok">Auto-fix</span> : <span className="pill manual">Manual Fix</span>}
        </div>
        <strong>{g.message}</strong>
        {meta.description && <p className="issue-type-desc">{meta.description}</p>}
        {g.recommendation && <p>{g.recommendation}</p>}
        {g.sample_urls?.length > 0 && <details><summary>Sample URLs</summary><ul>{g.sample_urls.map(u => <li key={u}><a href={u} target="_blank">{u}</a></li>)}</ul></details>}
      </div>
    </div>;
  })}</div>;
}

function HistoryTab({ history }) {
  return <section className="card"><h2><History size={20} /> Audit History</h2><div className="responsive-table"><table><thead><tr><th>Date</th><th>Status</th><th>Overall</th><th>SEO</th><th>Performance</th><th>Critical</th><th>Warnings</th></tr></thead><tbody>{history.map(h => <tr key={h.id}><td>{formatDate(h.started_at)}</td><td>{h.status}</td><td>{h.overall_score}</td><td>{h.seo_score}</td><td>{h.performance_score}</td><td>{h.critical_count}</td><td>{h.warning_count}</td></tr>)}</tbody></table></div></section>;
}

// Issue types that have a registered rollback handler. Must match the
// keys in ROLLBACK_DISPATCH in functions/api/sites/[id]/fix.ts. A Revert
// button is shown only for issues whose type is in this set AND whose
// status is fixed_pending_reaudit (i.e. an auto-fix has been applied
// and not yet verified). The Revert button calls onRevertIssue with the
// issue; the parent handler POSTs method=rollback to /api/sites/{id}/fix.
const REVERTABLE_ISSUE_TYPES = new Set(['canonical']);

function IssueList({ issues, showFix = false, onFixIssue, onRevertIssue, onVerifyIssue }) {
  if (!issues?.length) return <p className="muted">No issues found here.</p>;
  return <div className="issue-list">{issues.map((i, idx) => {
    const canFix = Number(i.auto_fixable) === 1;
    const isFixed = i.status === 'fixed_pending_reaudit';
    const canRevert = isFixed && REVERTABLE_ISSUE_TYPES.has(i.issue_type);
    // Bug #3: a Verify Fix button appears for any fixed_pending_
    // reaudit issue that has a page_url. The button hits the
    // single-URL verify endpoint and updates the issue status
    // without a full re-audit. Sitewide issues (page_url IS NULL)
    // do not get a Verify button — they need the full corpus.
    const canVerify = isFixed && !!i.page_url;
    const meta = issueTypeMeta(i.issue_type);
    const { Icon } = meta;
    return <div className="issue-card" key={i.id || idx}>
      <span className={`dot ${i.severity}`}></span>
      <div>
        <div className="issue-meta">
          <span className={`severity ${i.severity}`}>{i.severity}</span>
          <span className="issue-type"><Icon size={14} /> {meta.label}</span>
          {canFix ? <span className="pill ok">Auto-fix</span> : <span className="pill manual">Manual Fix</span>}
          {isFixed && <span className="pill muted-pill">Awaiting verification</span>}
        </div>
        <strong>{i.message}</strong>
        {meta.description && <p className="issue-type-desc">{meta.description}</p>}
        {i.recommendation && <p>{i.recommendation}</p>}
        {i.page_url && <a href={i.page_url} target="_blank">{i.page_url}</a>}
      </div>
      {showFix && canFix && !isFixed && <button onClick={() => onFixIssue?.(i)}>Fix</button>}
      {showFix && canRevert && <button className="revert-btn" onClick={() => onRevertIssue?.(i)}>Revert</button>}
      {showFix && canVerify && <button className="verify-btn" onClick={() => onVerifyIssue?.(i)}>Verify Fix</button>}
    </div>;
  })}</div>;
}

function PageMiniList({ pages }) {
  if (!pages?.length) return <p className="muted">No pages crawled yet.</p>;
  return <div className="mini-list">{pages.map(p => <div key={p.url}><span className={`score-badge ${scoreClass(p.score)}`}>{p.score}</span><div><strong>{p.label}</strong><small>{p.issues?.length ?? p.issue_count ?? 0} issues · HTTP {p.http_status}</small></div></div>)}</div>;
}


function SpeedSummaryCard({ title, strategy, data, active, onOpen }) {
  return <button className={`card speed-summary-card ${active ? 'active' : ''}`} onClick={onOpen}>
    <span>{title} PageSpeed</span>
    <strong className={scoreClass(data?.score)}>{data?.score ?? '-'}</strong>
    <small>{data?.tested_at ? `Tested ${formatDate(data.tested_at)}` : `No ${strategy} result yet`}</small>
  </button>;
}

function SpeedCard({ title, data, error }) {
  return <div className="card pagespeed-card"><h2><Gauge size={20} /> {title} PageSpeed</h2>{!data ? <p className="muted">No PageSpeed result yet. Click Run {title} to test PageSpeed from your browser. {error ? `Latest note: ${error}` : ''}</p> : <><div className={`big-score ${scoreClass(data.score)}`}>{data.score ?? '-'}<span>Performance</span></div><div className="metric-grid"><Metric label="FCP" value={data.fcp} /><Metric label="LCP" value={data.lcp} /><Metric label="TBT" value={data.tbt} /><Metric label="CLS" value={data.cls} /><Metric label="Speed Index" value={data.speed_index} /></div><h3>Top Opportunities</h3><AuditFindingList items={data.opportunities || []} empty="No major opportunities returned." /><h3>Diagnostics</h3><AuditFindingList items={data.diagnostics || []} empty="No major diagnostics returned." /></>}</div>;
}

function AuditFindingList({ items, empty }) {
  if (!items?.length) return <p className="muted">{empty}</p>;
  return <div className="finding-list">{items.map((o, idx) => <div key={idx} className="finding-row"><CheckCircle2 size={16} /><div><strong>{o.title}</strong>{o.displayValue && <small>{o.displayValue}</small>}{o.description && <p>{o.description}</p>}</div></div>)}</div>;
}

function Metric({ label, value }) { return <div className="metric"><strong>{value || '-'}</strong><span>{label}</span></div>; }
function StatusLine({ ok, label }) { return <div className={ok ? 'status-ok' : 'status-bad'}>{ok ? '✓' : '✕'} {label}</div>; }
function scoreClass(score) { return Number(score) >= 80 ? 'good' : Number(score) >= 60 ? 'warn' : 'bad'; }
function formatDate(v) { return v ? new Date(v).toLocaleString() : '-'; }

// Compact pagination control. Used by every table that fetches a paginated
// resource (Pages tab, Actions tab, Broken Links tab). Renders as
// "‹ Prev  1 2 3 … 8  Next ›" with a status line ("Showing 1–50 of 395").
// The page numbers collapse to first/last + neighbours when total_pages > 7
// so the bar never wraps awkwardly on small screens.
function Pagination({ meta, onPageChange, loading }) {
  if (!meta || meta.total <= meta.limit) {
    return meta?.total > 0
      ? <div className="pagination"><span className="muted">Showing all {meta.total} {meta.total === 1 ? 'row' : 'rows'}</span></div>
      : null;
  }
  const { page, limit, total, total_pages } = meta;
  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  const setPage = (p) => { if (p >= 1 && p <= total_pages && p !== page) onPageChange(p); };
  const pages = buildPageList(page, total_pages);
  return <div className={`pagination ${loading ? 'loading' : ''}`}>
    <span className="muted">Showing {start}–{end} of {total}</span>
    <div className="pagination-controls">
      <button type="button" disabled={page <= 1 || loading} onClick={() => setPage(page - 1)} aria-label="Previous page">
        <ChevronLeft size={14} /> Prev
      </button>
      {pages.map((p, i) => p === '…'
        ? <span key={`gap-${i}`} className="pagination-gap">…</span>
        : <button key={p} type="button" className={p === page ? 'active' : ''} disabled={loading} onClick={() => setPage(p)}>{p}</button>
      )}
      <button type="button" disabled={page >= total_pages || loading} onClick={() => setPage(page + 1)} aria-label="Next page">
        Next <ChevronRight size={14} />
      </button>
    </div>
  </div>;
}

// Collapse long page lists: [1, 2, 3, …, 8] for 8-page lists, etc.
// Always show first, last, current, and 1 neighbour on each side.
function buildPageList(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, current, current - 1, current + 1]);
  const list = Array.from(pages).filter(p => p >= 1 && p <= total).sort((a, b) => a - b);
  const out = [];
  for (let i = 0; i < list.length; i++) {
    if (i > 0 && list[i] - list[i - 1] > 1) out.push('…');
    out.push(list[i]);
  }
  return out;
}

createRoot(document.getElementById('root')).render(<App />);
